import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { DeleteIcon, WebhookIcon } from '../components/icons';

/**
 * Project-scoped webhooks.
 *
 * Mounted at /projects/<slug>/webhooks.  Every subscription minted
 * from this page is pinned to the current project so its events
 * only fire for content in that scope — a leaky webhook on one
 * project can't spam the whole tenant.
 */
export function Webhooks() {
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const projectId = project?.project_id;
  const api = useMemo(
    () => createApiClient(getToken, { projectId }),
    [getToken, projectId],
  );

  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmNode, ask] = useConfirm();

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api(`/v1/admin/webhooks?project_id=${encodeURIComponent(projectId)}`);
      setWebhooks(resp?.webhooks || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>Webhooks</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          HMAC-signed HTTP callbacks fired when content events happen
          — published, dismissed, completed. Scoped to this project,
          so events on other projects can't reach your receiver.
        </p>
      </div>

      <CreateWebhookForm api={api} projectId={projectId} onCreated={load} />

      {loading && <div className="state"><div className="state__title">Loading webhooks…</div></div>}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load webhooks</div>
          <div>{error}</div>
        </div>
      )}

      {!loading && !error && (
        webhooks.length === 0 ? (
          <div className="state">
            <div className="state__title">No webhooks yet</div>
            <div>Add one above to start receiving event deliveries.</div>
          </div>
        ) : (
          <ul className="list">
            {webhooks.map((w) => (
              <li key={w.webhook_id} className="list__row">
                <div className="list__primary">
                  <span className="list__name">
                    {w.name}
                    {!w.active && <span className="badge badge--default" style={{ marginLeft: 8 }}>Paused</span>}
                  </span>
                  <span className="list__meta">
                    <code>{w.url}</code>
                    {' · '}
                    events: {(w.event_types || []).join(', ') || '*'}
                    {w.delivery_count !== undefined && (
                      <> {' · '} {w.delivery_count} delivered / {w.failure_count || 0} failed</>
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => ask({
                    title: `Delete webhook "${w.name}"?`,
                    body: 'Future events stop firing immediately.',
                    confirmLabel: 'Delete',
                    danger: true,
                    runOnConfirm: async () => {
                      await api(`/v1/admin/webhooks/${w.webhook_id}`, { method: 'DELETE' });
                      load();
                    },
                  })}
                >
                  <DeleteIcon /> Delete
                </button>
              </li>
            ))}
          </ul>
        )
      )}

      {confirmNode}
    </section>
  );
}

function CreateWebhookForm({ api, projectId, onCreated }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [justCreated, setJustCreated] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setJustCreated(null);
    try {
      const created = await api('/v1/admin/webhooks', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          url: url.trim(),
          project_id: projectId || '',
        }),
      });
      setName('');
      setUrl('');
      setJustCreated(created);
      onCreated();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3 className="card__title">Register a new webhook</h3>
      <form
        onSubmit={submit}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr auto',
          gap: 12,
          alignItems: 'end',
          marginTop: 10,
        }}
      >
        <label className="field">
          <span className="field__label">Name</span>
          <input
            className="input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="PostHog forwarder"
          />
        </label>
        <label className="field">
          <span className="field__label">Delivery URL</span>
          <input
            className="input"
            required
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hooks.yourcompany.com/spotlight"
          />
        </label>
        <button type="submit" disabled={busy} className="btn btn--primary">
          <WebhookIcon /> {busy ? 'Creating…' : 'Create webhook'}
        </button>
      </form>
      {err && <p style={{ color: 'var(--danger-700)', marginTop: 12 }}>{err}</p>}
      {justCreated && (
        <div className="state" style={{ marginTop: 16, textAlign: 'left' }}>
          <div className="state__title">Save this secret now — it won't be shown again</div>
          <code style={{ wordBreak: 'break-all', display: 'block', marginTop: 8 }}>
            {justCreated.secret || '(secret not returned — check backend logs)'}
          </code>
        </div>
      )}
    </section>
  );
}
